(() => {
  const elementName = 'gh-category-slider';

  if (customElements.get(elementName)) return;

  class GhCategorySlider extends HTMLElement {
    constructor() {
      super();

      this.currentPage = 0;
      this.pageStarts = [0];
      this.scrollFrame = null;
      this.resizeObserver = null;

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.refresh = this.refresh.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-gh-category-viewport]');
      this.track = this.querySelector('[data-gh-category-track]');
      this.slides = [...this.querySelectorAll('[data-gh-category-slide]')];
      this.previousButton = this.querySelector('[data-gh-category-previous]');
      this.nextButton = this.querySelector('[data-gh-category-next]');
      this.controls = this.querySelector('[data-gh-category-controls]');
      this.progress = this.querySelector('[data-gh-category-progress]');
      this.currentOutput = this.querySelector('[data-gh-category-current]');
      this.totalOutput = this.querySelector('[data-gh-category-total]');
      this.status = this.querySelector('[data-gh-category-status]');
      this.progressEnabled = this.dataset.progressEnabled === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.viewport || !this.track) return;

      this.previousButton?.addEventListener('click', this.handlePreviousClick);
      this.nextButton?.addEventListener('click', this.handleNextClick);
      this.viewport.addEventListener('keydown', this.handleKeydown);
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.addEventListener('shopify:block:select', this.handleBlockSelect);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.refresh);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.refresh);
      }

      requestAnimationFrame(this.refresh);
    }

    disconnectedCallback() {
      if (!this.viewport) return;

      this.viewport.removeEventListener('keydown', this.handleKeydown);
      this.viewport.removeEventListener('scroll', this.handleScroll);
      this.previousButton?.removeEventListener('click', this.handlePreviousClick);
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.refresh);

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    refresh() {
      this.slides = [...this.querySelectorAll('[data-gh-category-slide]')];

      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const hasOverflow = maxScroll > 1 && this.slides.length > 1;
      const visibleSlides = this.getVisibleSlideCount();
      const pageCount = hasOverflow ? Math.ceil(this.slides.length / visibleSlides) : 1;
      const lastStart = Math.max(0, this.slides.length - visibleSlides);
      const starts = [];

      for (let page = 0; page < pageCount; page += 1) {
        const start = Math.min(page * visibleSlides, lastStart);
        if (!starts.includes(start)) starts.push(start);
      }

      this.pageStarts = starts.length ? starts : [0];
      this.toggleAttribute('data-has-overflow', hasOverflow);

      if (this.previousButton) this.previousButton.hidden = !hasOverflow;
      if (this.nextButton) this.nextButton.hidden = !hasOverflow;
      if (this.controls) this.controls.hidden = !hasOverflow && !this.progressEnabled;

      this.updateCurrentPage();
    }

    getVisibleSlideCount() {
      if (this.slides.length < 2) return Math.max(1, this.slides.length);

      const firstOffset = this.slides[0].offsetLeft;
      const secondOffset = this.slides[1].offsetLeft;
      const step = Math.abs(secondOffset - firstOffset);

      if (!step) return 1;

      return Math.max(1, Math.round(this.viewport.clientWidth / step));
    }

    handleScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateCurrentPage();
      });
    }

    handlePreviousClick() {
      this.goToPage(this.currentPage - 1);
    }

    handleNextClick() {
      this.goToPage(this.currentPage + 1);
    }

    handleKeydown(event) {
      let nextPage = null;

      switch (event.key) {
        case 'ArrowLeft':
          nextPage = this.currentPage - 1;
          break;
        case 'ArrowRight':
          nextPage = this.currentPage + 1;
          break;
        case 'Home':
          nextPage = 0;
          break;
        case 'End':
          nextPage = this.pageStarts.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.goToPage(nextPage);
    }

    handleBlockSelect(event) {
      const slide = event.target.closest?.('[data-gh-category-slide]');
      if (!slide || !this.contains(slide)) return;

      const slideIndex = this.slides.indexOf(slide);
      if (slideIndex < 0) return;

      const pageIndex = this.pageStarts.reduce((selectedPage, start, index) => {
        return start <= slideIndex ? index : selectedPage;
      }, 0);

      this.goToPage(pageIndex, false);
    }

    goToPage(pageIndex, focusControl = true) {
      if (!this.slides.length) return;

      const boundedPage = Math.max(0, Math.min(pageIndex, this.pageStarts.length - 1));
      const target = this.slides[this.pageStarts[boundedPage]];
      if (!target) return;

      target.scrollIntoView({
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });

      this.currentPage = boundedPage;
      this.updateControls();

      if (focusControl) {
        if (boundedPage === 0) this.nextButton?.focus();
        if (boundedPage === this.pageStarts.length - 1) this.previousButton?.focus();
      }
    }

    updateCurrentPage() {
      if (!this.slides.length) {
        this.currentPage = 0;
        this.updateControls();
        return;
      }

      const viewportRect = this.viewport.getBoundingClientRect();
      const isRtl = getComputedStyle(this.viewport).direction === 'rtl';
      let nearestSlide = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const distance = isRtl
          ? Math.abs(viewportRect.right - slideRect.right)
          : Math.abs(slideRect.left - viewportRect.left);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestSlide = index;
        }
      });

      this.currentPage = this.pageStarts.reduce((selectedPage, start, index) => {
        return Math.abs(start - nearestSlide) < Math.abs(this.pageStarts[selectedPage] - nearestSlide)
          ? index
          : selectedPage;
      }, 0);

      this.updateControls();
    }

    updateControls() {
      const totalPages = Math.max(1, this.pageStarts.length);
      const currentPageNumber = Math.min(this.currentPage + 1, totalPages);
      const isFirst = currentPageNumber === 1;
      const isLast = currentPageNumber === totalPages;

      this.setButtonState(this.previousButton, isFirst);
      this.setButtonState(this.nextButton, isLast);

      if (this.currentOutput) this.currentOutput.textContent = String(currentPageNumber);
      if (this.totalOutput) this.totalOutput.textContent = String(totalPages);

      if (this.progress) {
        this.progress.style.setProperty('--gh-category-progress', String(currentPageNumber / totalPages));
        this.progress.setAttribute('aria-valuemax', String(totalPages));
        this.progress.setAttribute('aria-valuenow', String(currentPageNumber));
        this.progress.setAttribute('aria-valuetext', `${currentPageNumber} / ${totalPages}`);
      }

      if (this.status) {
        const label = this.status.dataset.label || 'Carousel';
        const message = `${label}: ${currentPageNumber} / ${totalPages}`;
        if (this.status.textContent !== message) this.status.textContent = message;
      }
    }

    setButtonState(button, disabled) {
      if (!button) return;

      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    }
  }

  customElements.define(elementName, GhCategorySlider);
})();
